import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  Discount,
  PatchAdminV1StoresStoreIdDiscountsIdBody,
  PostAdminV1StoresStoreIdDiscountsBody,
  PostAdminV1StoresStoreIdDiscountsIdCodesBody,
} from '@nucleus-commerce/api-client/admin';

import { discountsApi } from '../api/discounts-api';

import { discountKeys } from './discount-keys';

async function remember(
  queryClient: ReturnType<typeof useQueryClient>,
  storeId: string,
  discount: Discount,
) {
  queryClient.setQueryData(discountKeys.detail(storeId, discount.id), discount);
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: discountKeys.lists(storeId) }),
    queryClient.invalidateQueries({ queryKey: discountKeys.allCodes(storeId, discount.id) }),
  ]);
}

/**
 * The discount's preview codes and `codeCount` change with a code, so the detail is refetched
 * with the code pages.
 */
function refreshCodes(
  queryClient: ReturnType<typeof useQueryClient>,
  storeId: string,
  discountId: string,
) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: discountKeys.detail(storeId, discountId) }),
    queryClient.invalidateQueries({ queryKey: discountKeys.lists(storeId) }),
  ]);
}

/** 409 ALREADY_EXISTS on `body.codes` when a code is taken in the store; the form shows it. */
export function useCreateDiscount(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdDiscountsBody) => discountsApi.create(storeId, body),
    meta: { successMessage: 'discounts:toasts.created', silentError: true },
    onSuccess: (discount) => remember(queryClient, storeId, discount),
  });
}

export function useUpdateDiscount(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchAdminV1StoresStoreIdDiscountsIdBody }) =>
      discountsApi.update(storeId, id, body),
    meta: { successMessage: 'discounts:toasts.updated', silentError: true },
    onSuccess: (discount) => remember(queryClient, storeId, discount),
  });
}

export function useDeleteDiscount(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => discountsApi.remove(storeId, id),
    meta: { successMessage: 'discounts:toasts.deleted', silentError: true },
    onSuccess: (_void, id) => {
      queryClient.removeQueries({ queryKey: discountKeys.detail(storeId, id) });
      return queryClient.invalidateQueries({ queryKey: discountKeys.lists(storeId) });
    },
  });
}

export function useAddDiscountCodes(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: PostAdminV1StoresStoreIdDiscountsIdCodesBody;
    }) => discountsApi.addCodes(storeId, id, body),
    meta: { successMessage: 'discounts:toasts.codesAdded', silentError: true },
    onSuccess: (discount) => remember(queryClient, storeId, discount),
  });
}

export function useUpdateDiscountCode(storeId: string, discountId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ codeId, isActive }: { codeId: string; isActive: boolean }) =>
      discountsApi.updateCode(storeId, discountId, codeId, { isActive }),
    meta: { silentError: true },
    onSuccess: () => refreshCodes(queryClient, storeId, discountId),
  });
}

/** 409 RESOURCE_IN_USE once a code was used on an order, 409 CONFLICT for the last code. */
export function useDeleteDiscountCode(storeId: string, discountId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (codeId: string) => discountsApi.removeCode(storeId, discountId, codeId),
    meta: { successMessage: 'discounts:toasts.codeDeleted', silentError: true },
    onSuccess: () => refreshCodes(queryClient, storeId, discountId),
  });
}

export function useExportDiscountCodes(storeId: string, discountId: string) {
  return useMutation({
    mutationFn: () => discountsApi.exportCodes(storeId, discountId),
    meta: { silentError: true },
  });
}
